// MP219 — Alert box / callout primitive.
//
// Consolidates ~54 inline patterns like
//   `rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900`
// scattered across apps/web for error / success / warning / info
// callouts. Optionally dismissible (auto-dismiss for success); the
// pattern matches DismissibleAlert from MP218 but lifted into the
// shared package so internal consoles can use it too.

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/cn';

const alertBoxVariants = cva(
  'flex items-start gap-2 rounded-md border-l-4 p-3 text-sm transition-opacity',
  {
    variants: {
      tone: {
        info: 'border-blue-300 bg-blue-50 text-blue-900 dark:border-blue-700 dark:bg-blue-950/30 dark:text-blue-200',
        success:
          'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-200',
        warning:
          'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-200',
        danger:
          'border-rose-300 bg-rose-50 text-rose-900 dark:border-rose-700 dark:bg-rose-950/30 dark:text-rose-200',
      },
    },
    defaultVariants: { tone: 'info' },
  },
);

export interface AlertBoxProps
  // `title` on a DOM div is `string | undefined`; we want ReactNode so
  // the omit-and-redeclare pattern.
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>,
    VariantProps<typeof alertBoxVariants> {
  /** Optional icon node (lucide-react component) rendered before the
   *  content slot. */
  icon?: React.ReactNode;
  /** When set, renders an × button + auto-dismisses success alerts
   *  after `autoDismissMs` (default 4000 for success, never for the
   *  other tones). Caller is responsible for clearing the source
   *  state in onDismiss. */
  onDismiss?: () => void;
  autoDismissMs?: number;
  /** Optional title rendered bold above the content. */
  title?: React.ReactNode;
}

export const AlertBox = React.forwardRef<HTMLDivElement, AlertBoxProps>(
  (
    { className, tone, icon, onDismiss, autoDismissMs, title, children, ...props },
    ref,
  ) => {
    const [hidden, setHidden] = React.useState(false);
    const effectiveAutoDismiss =
      autoDismissMs ?? (tone === 'success' ? 4000 : 0);

    React.useEffect(() => {
      if (effectiveAutoDismiss <= 0 || !onDismiss) return;
      const t = window.setTimeout(() => {
        setHidden(true);
        onDismiss();
      }, effectiveAutoDismiss);
      return () => window.clearTimeout(t);
    }, [effectiveAutoDismiss, onDismiss]);

    if (hidden) return null;

    return (
      <div
        ref={ref}
        role={tone === 'danger' ? 'alert' : 'status'}
        className={cn(alertBoxVariants({ tone }), className)}
        {...props}
      >
        {icon && <span className="mt-0.5 shrink-0">{icon}</span>}
        <div className="flex-1">
          {title && <div className="font-semibold">{title}</div>}
          {children}
        </div>
        {onDismiss && (
          <button
            type="button"
            onClick={() => {
              setHidden(true);
              onDismiss();
            }}
            aria-label="Dismiss"
            className="-mr-1 rounded p-0.5 text-current opacity-60 hover:bg-black/5 hover:opacity-100"
          >
            <svg
              className="h-3.5 w-3.5"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}
      </div>
    );
  },
);
AlertBox.displayName = 'AlertBox';

export { alertBoxVariants };
